import client from './client';
import { Category } from './categories';

export interface Product {
  id: number;
  store_id: number;
  name: string;
  description?: string;
  price: number;
  mrp?: number;
  stock_quantity: number;
  category?: string;
  category_id?: number;
  unit: string;
  weight?: string;
  image_url?: string;
  thumbnail_url?: string;
  tags: string[];
  is_halal: boolean;
  is_veg: boolean;
  is_available: boolean;
  created_at: string;
  category_info?: Category;
  discount_percent?: number;
  master_item_id?: number;
  effective_discount_percent?: number;
  effective_price?: number;
}

export interface ProductCreate {
  name: string;
  description?: string;
  price: number;
  mrp?: number;
  stock_quantity: number;
  category?: string;
  category_id?: number;
  unit?: string;
  weight?: string;
  image_url?: string;
  thumbnail_url?: string;
  tags?: string[];
  is_halal?: boolean;
  is_veg?: boolean;
}

export const productsAPI = {
  async createProduct(data: ProductCreate): Promise<Product> {
    const response = await client.post<Product>('/api/products', data);
    return response.data;
  },

  async getProduct(productId: number): Promise<Product> {
    const response = await client.get<Product>(`/api/products/${productId}`);
    return response.data;
  },

  async listProducts(
    storeId: number,
    skip: number = 0,
    limit: number = 100,
    availableOnly: boolean = false
  ): Promise<Product[]> {
    const response = await client.get<Product[]>('/api/products', {
      params: { store_id: storeId, skip, limit, available_only: availableOnly }
    });
    return response.data;
  },

  async updateProduct(productId: number, data: Partial<ProductCreate>): Promise<Product> {
    const response = await client.put<Product>(`/api/products/${productId}`, data);
    return response.data;
  },

  async deleteProduct(productId: number): Promise<any> {
    const response = await client.delete(`/api/products/${productId}`);
    return response.data;
  },

  async updateStock(productId: number, quantityChange: number): Promise<any> {
    const response = await client.patch(`/api/products/${productId}/stock`, null, {
      params: { quantity_change: quantityChange }
    });
    return response.data;
  },

  async searchByName(
    storeId: number,
    name: string,
    skip: number = 0,
    limit: number = 100
  ): Promise<Product[]> {
    const response = await client.get<Product[]>('/api/products/search/name', {
      params: { store_id: storeId, name, skip, limit }
    });
    return response.data;
  },

  async searchByCategory(
    storeId: number,
    category: string,
    skip: number = 0,
    limit: number = 100
  ): Promise<Product[]> {
    const response = await client.get<Product[]>('/api/products/search/category', {
      params: { store_id: storeId, category, skip, limit }
    });
    return response.data;
  },

  async searchByTags(
    storeId: number,
    tags: string[],
    skip: number = 0,
    limit: number = 100
  ): Promise<Product[]> {
    const response = await client.post<Product[]>('/api/products/search/tags', null, {
      params: { 
        store_id: storeId, 
        tags: tags.join(','),
        skip, 
        limit 
      }
    });
    return response.data;
  },

  async bulkImportProducts(file: File): Promise<{ message: string; created: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.post('/api/products/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async search(params: {
    store_id?: number;
    q?: string;
    category?: string;
    category_id?: number;
    min_price?: number;
    max_price?: number;
    is_veg?: boolean;
    is_halal?: boolean;
    available_only?: boolean;
    sort_by?: 'relevance' | 'price_asc' | 'price_desc' | 'newest';
    skip?: number;
    limit?: number;
  }): Promise<Product[]> {
    const response = await client.get<Product[]>('/api/products/search', { params });
    return response.data;
  },

  async autocomplete(query: string, storeId?: number): Promise<string[]> {
    const response = await client.get<{ suggestions: string[] }>('/api/products/autocomplete', {
      params: { q: query, store_id: storeId },
    });
    return response.data.suggestions;
  },
};
